import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReplayIcon from '@mui/icons-material/Replay';

import { LeftPanel } from '@/components/common';

import { useGetRefrigerationUnitModelsQuery } from '@/graphql/GetRefrigerationUnitModels.generated';

import { appStore, reportRequestError, retryAfterRequestError } from '@/store';

import {
  clearAssetSelection,
  setRefrigerationUnitModels,
  store,
  unselectAllRefrigerationUnitModels,
} from '../store';

import { ManufacturerAvatarList } from './ManufacturerAvatarList';
import { RefrigerationUnitModelsFilterList } from './RefrigerationUnitModelsFilterList';

interface RefrigerationUnitModelsFilterPanelProps {
  openedWidth: number;
  closedWidth: number;
}

function RefrigerationUnitModelsFilterPanel(
  props: RefrigerationUnitModelsFilterPanelProps,
) {
  const { openedWidth, closedWidth } = props;
  const { t } = useTranslation();
  const appSnap = useSnapshot(appStore);
  const snap = useSnapshot(store);

  const [{ data, fetching, error }, reexecuteQuery] =
    useGetRefrigerationUnitModelsQuery({
      pause: snap.refrigerationUnitModels.length > 0,
    });

  const [errorId, setErrorId] = useState<string | null>(null);

  useEffect(() => {
    if (data && data.container_insights_refrigeration_unit_models) {
      setRefrigerationUnitModels(
        data.container_insights_refrigeration_unit_models,
      );
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      setErrorId(reportRequestError(error, reexecuteQuery));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleRetry = () => {
    retryAfterRequestError(errorId);
    setErrorId(null);
  };

  const [open, setOpen] = useState(true);
  const handleOpen = () => setOpen(!open);

  const handleClear = () => {
    unselectAllRefrigerationUnitModels();
    clearAssetSelection();
  };

  return (
    <LeftPanel
      open={open}
      onClick={handleOpen}
      openedWidth={openedWidth}
      closedWidth={closedWidth}
      title={
        <Stack direction="row" alignItems="center" flexGrow={1} spacing={1}>
          <Typography sx={{ pt: 0 }}>{t('models')}</Typography>
          {fetching && <CircularProgress size={24} />}
          {!fetching && appSnap.hasRequestError(errorId) && (
            <Tooltip title="Retry">
              <IconButton onClick={handleRetry}>
                <ReplayIcon />
              </IconButton>
            </Tooltip>
          )}
          <Button
            size="small"
            endIcon={<CloseIcon />}
            sx={{
              visibility: snap.refrigerationUnitModels.some(
                (model) => model.selected,
              )
                ? 'visible'
                : 'hidden',
            }}
            onClick={handleClear}
          >
            Clear
          </Button>
        </Stack>
      }
    >
      {/* force right margin to hide panel content when closed */}
      {open ? (
        <Stack
          direction="column"
          sx={{
            mr: 0,
          }}
        >
          {!fetching && appSnap.hasRequestError(errorId) && (
            <FormHelperText
              sx={(theme) => ({
                color: theme.palette.error.main,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                marginLeft: 2,
              })}
            >
              <ErrorOutlineIcon fontSize="small" />
              Error while fetching models
            </FormHelperText>
          )}
          <RefrigerationUnitModelsFilterList />
        </Stack>
      ) : (
        <Stack direction="column" alignItems="flex-end" sx={{ mt: 1 }}>
          <ManufacturerAvatarList onClick={handleOpen} />
        </Stack>
      )}
    </LeftPanel>
  );
}

export { RefrigerationUnitModelsFilterPanel };
